/**
 * RequirementMatchSection — Consolidated Requirement Match panel.
 *
 * Thin presentation layer over two existing canonical services:
 *   • Phase 2C  GET /requirement-matches        — persisted match records (suggested, confirmed)
 *   • Phase 1C  GET /matches                    — formal match[] entries + linked_document_id catch
 *
 * All actions go through Phase 2C endpoints (upsert-safe; Phase 1C linked catches work too):
 *   • Confirm  POST /requirement-matches/{rid}/confirm
 *   • Reject   POST /requirement-matches/{rid}/reject
 *   • Unlink   POST /requirement-matches/{rid}/reject  (reason = "Staff unlinked from viewer")
 *   • Generate POST /requirement-matches/generate
 *
 * No new matching logic. No live re-scoring. No merger of backend systems.
 */
import { useCallback, useEffect, useState } from "react";
import * as Icons from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

const chip = (s) => (s || "").replace(/_/g, " ");

const BAND = {
  very_high: { label: "Very High", tone: "bg-emerald-100 text-emerald-700" },
  high:      { label: "High",      tone: "bg-blue-100  text-blue-700"    },
  medium:    { label: "Medium",    tone: "bg-amber-100 text-amber-700"   },
  low:       { label: "Low",       tone: "bg-orange-100 text-orange-700" },
  none:      { label: "None",      tone: "bg-slate-100 text-slate-500"   },
};

export function RequirementMatchSection({ docId, onRefresh }) {
  const [matches2c, setMatches2c]   = useState([]);  // Phase 2C: /requirement-matches
  const [map1c, setMap1c]           = useState({});  // Phase 1C: request_id → {request_status}
  const [linked1c, setLinked1c]     = useState([]);  // Phase 1C: linked_document_id catches (no Phase 2C record)
  const [loading, setLoading]       = useState(true);
  const [generating, setGenerating] = useState(false);
  const [busy, setBusy]             = useState({});
  const [expanded, setExpanded]     = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [r2c, r1c] = await Promise.all([
        api.get(`/documents-intel/${docId}/requirement-matches`).then((r) => r.data.matches || []).catch(() => []),
        api.get(`/documents-intel/${docId}/matches`).then((r) => r.data.matches || []).catch(() => []),
      ]);
      setMatches2c(r2c);
      // Build request_id → Phase 1C item for request_status lookup on confirmed items
      const m = {};
      r1c.forEach((item) => { m[item.request_id] = item; });
      setMap1c(m);
      // Phase 1C linked_document_id catches: items not already tracked in Phase 2C
      const p2cIds = new Set(r2c.map((m) => m.request_id));
      setLinked1c(r1c.filter((item) => !p2cIds.has(item.request_id) && (item.matches || []).length === 0));
    } finally {
      setLoading(false);
    }
  }, [docId]);

  useEffect(() => { load(); }, [load]);

  const doAction = async (action, requestId) => {
    setBusy((b) => ({ ...b, [requestId]: true }));
    try {
      if (action === "confirm") {
        await api.post(`/documents-intel/${docId}/requirement-matches/${requestId}/confirm`);
        toast.success("Match confirmed — request satisfied");
      } else if (action === "reject") {
        await api.post(`/documents-intel/${docId}/requirement-matches/${requestId}/reject`);
        toast.message("Match rejected");
      } else if (action === "unlink") {
        await api.post(`/documents-intel/${docId}/requirement-matches/${requestId}/reject`, { reason: "Staff unlinked from viewer" });
        toast.message("Unlinked");
      }
      load(); onRefresh?.();
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(d?.message || (typeof d === "string" ? d : "Action failed"));
    } finally {
      setBusy((b) => ({ ...b, [requestId]: false }));
    }
  };

  const generate = async () => {
    setGenerating(true);
    try {
      const { data } = await api.post(`/documents-intel/${docId}/requirement-matches/generate`);
      setMatches2c(data.matches || []);
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(d?.message || (typeof d === "string" ? d : "Confirm a classification first"));
    } finally {
      setGenerating(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-xs text-slate-400 py-2" data-testid="requirement-match-loading">
        <Icons.Loader2 className="h-3.5 w-3.5 animate-spin" />Loading…
      </div>
    );
  }

  const confirmed2c = matches2c.filter((m) => m.status === "confirmed");
  const suggested2c = matches2c.filter((m) => m.status === "suggested");
  const hasConfirmed = confirmed2c.length > 0 || linked1c.length > 0;

  return (
    <div className="space-y-3 text-xs" data-testid="requirement-match-section">

      {/* ── Confirmed ── */}
      {hasConfirmed && (
        <div className="space-y-0" data-testid="match-confirmed-group">
          {confirmed2c.map((m) => {
            const reqStatus = map1c[m.request_id]?.request_status;
            return (
              <div key={m.request_id} className="flex items-center justify-between gap-2 py-1.5 border-b border-slate-50 last:border-0" data-testid={`match-confirmed-${m.request_id}`}>
                <div className="min-w-0">
                  <div className="flex items-center gap-1.5">
                    <Icons.CheckCircle2 className="h-3 w-3 text-emerald-500 shrink-0" />
                    <span className="text-slate-700 font-medium truncate">{m.request_title}</span>
                  </div>
                  {reqStatus && (
                    <div className="text-[10px] text-slate-400 capitalize ml-[18px]">{chip(reqStatus)}</div>
                  )}
                </div>
                <button
                  onClick={() => doAction("unlink", m.request_id)}
                  disabled={busy[m.request_id]}
                  className="shrink-0 text-[10px] border border-slate-200 rounded px-2 py-0.5 text-slate-500 hover:text-rose-600 hover:border-rose-200 disabled:opacity-50"
                  data-testid={`match-unlink-${m.request_id}`}
                >
                  {busy[m.request_id] ? <Icons.Loader2 className="h-3 w-3 animate-spin inline" /> : "Unlink"}
                </button>
              </div>
            );
          })}
          {/* Phase 1C linked_document_id catches — not yet in Phase 2C match records */}
          {linked1c.map((m) => (
            <div key={m.request_id} className="flex items-center justify-between gap-2 py-1.5 border-b border-slate-50 last:border-0" data-testid={`match-linked1c-${m.request_id}`}>
              <div className="min-w-0">
                <div className="flex items-center gap-1.5">
                  <Icons.Link className="h-3 w-3 text-blue-400 shrink-0" />
                  <span className="text-slate-700 font-medium truncate">{m.title}</span>
                </div>
                <div className="text-[10px] text-slate-400 ml-[18px]">Linked · <span className="capitalize">{chip(m.request_status)}</span></div>
              </div>
              <button
                onClick={() => doAction("unlink", m.request_id)}
                disabled={busy[m.request_id]}
                className="shrink-0 text-[10px] border border-slate-200 rounded px-2 py-0.5 text-slate-500 hover:text-rose-600 hover:border-rose-200 disabled:opacity-50"
                data-testid={`match-unlink1c-${m.request_id}`}
              >
                {busy[m.request_id] ? <Icons.Loader2 className="h-3 w-3 animate-spin inline" /> : "Unlink"}
              </button>
            </div>
          ))}
        </div>
      )}

      {/* ── Suggested ── */}
      {suggested2c.length > 0 && (
        <div className="space-y-2" data-testid="match-suggested-group">
          {suggested2c.map((m) => {
            const band = BAND[m.confidence_band] || BAND.none;
            const isOpen = expanded[m.request_id];
            return (
              <div key={m.request_id} className="rounded-lg border border-slate-100 p-2.5" data-testid={`match-suggested-${m.request_id}`}>
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-xs font-medium text-slate-800 truncate min-w-0 flex-1">{m.request_title}</span>
                  <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium shrink-0 ${band.tone}`} data-testid={`match-band-${m.request_id}`}>{band.label}</span>
                </div>
                <div className="text-[10px] text-slate-400 mt-0.5">{m.canonical_label} → {m.request_document_type}</div>

                {isOpen && (m.evidence || []).length > 0 && (
                  <div className="mt-1.5 text-[11px] text-slate-500 rounded bg-slate-50 border border-slate-100 px-2 py-1.5 space-y-0.5" data-testid={`match-evidence-${m.request_id}`}>
                    {m.evidence.map((e, i) => (
                      <div key={i} className="flex gap-1"><span className="text-slate-300">•</span>{e}</div>
                    ))}
                    {m.taxonomy_version != null && (
                      <div className="text-slate-400 pt-0.5">Taxonomy v{m.taxonomy_version} · deterministic</div>
                    )}
                  </div>
                )}

                <div className="flex gap-1.5 mt-2 items-center">
                  <button
                    onClick={() => doAction("confirm", m.request_id)}
                    disabled={busy[m.request_id]}
                    className="text-[10px] bg-emerald-600 text-white rounded px-2.5 py-1 font-medium disabled:opacity-50 flex items-center gap-1"
                    data-testid={`match-confirm-${m.request_id}`}
                  >
                    {busy[m.request_id] ? <Icons.Loader2 className="h-3 w-3 animate-spin" /> : <Icons.Check className="h-3 w-3" />}Confirm
                  </button>
                  <button
                    onClick={() => doAction("reject", m.request_id)}
                    disabled={busy[m.request_id]}
                    className="text-[10px] border border-slate-200 rounded px-2.5 py-1 disabled:opacity-50"
                    data-testid={`match-reject-${m.request_id}`}
                  >
                    Reject
                  </button>
                  <button
                    onClick={() => setExpanded((p) => ({ ...p, [m.request_id]: !isOpen }))}
                    className="text-[10px] text-slate-400 ml-auto inline-flex items-center gap-0.5 hover:text-slate-600"
                    data-testid={`match-why-${m.request_id}`}
                  >
                    <Icons.Info className="h-3 w-3" />{isOpen ? "Hide" : "Why"}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* ── Empty state ── */}
      {!hasConfirmed && suggested2c.length === 0 && (
        <div className="space-y-2" data-testid="match-empty">
          <div className="text-[11px] text-slate-400 italic">No requirement matched.</div>
          <button
            onClick={generate}
            disabled={generating}
            className="inline-flex items-center gap-1.5 text-[11px] bg-blue-600 text-white rounded-md px-2.5 py-1 disabled:opacity-50"
            data-testid="match-find-btn"
          >
            {generating ? <Icons.Loader2 className="h-3 w-3 animate-spin" /> : <Icons.Sparkles className="h-3 w-3" />}
            Find Matches
          </button>
        </div>
      )}

      {/* ── Find More — shown when confirmed exist but no suggestions yet ── */}
      {hasConfirmed && suggested2c.length === 0 && (
        <button
          onClick={generate}
          disabled={generating}
          className="inline-flex items-center gap-1.5 text-[10px] text-slate-400 hover:text-slate-600 disabled:opacity-50"
          data-testid="match-find-more-btn"
        >
          {generating ? <Icons.Loader2 className="h-3 w-3 animate-spin" /> : <Icons.Search className="h-3 w-3" />}
          Find More Matches
        </button>
      )}
    </div>
  );
}
